import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import type { AoiRemoteTimeWindow } from '@/types/flood.types';

/**
 * Thanh kéo tab Viễn thám — mô phỏng diện tích / mốc ngày trong UI.
 * Không thay thế sản phẩm phân tích SAR từ backend.
 */

const SLIDER_STEPS = 1000;

/**
 * 1000 bước (0..1000) trên thanh Ant Slider — bước mịn, không cần bước theo từng ngày.
 */
export { SLIDER_STEPS as REMOTE_SCRUB_SLIDER_STEPS };

/** Easing mượt [0,1] → [0,1]. */
export function smoothstep01(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
}

/**
 * `t` trong [0,1] dọc cửa sổ — cột đối chiếu: diện tích tăng dần từ gần 0 đến `maxKm2`.
 */
export function crisisFloodAreaKm2(maxKm2: number, tInWindow01: number): number {
  if (!Number.isFinite(maxKm2) || maxKm2 <= 0) return 0;
  const u = smoothstep01(tInWindow01);
  const minKm2 = Math.min(0.02, maxKm2 * 0.02);
  return minKm2 + (maxKm2 - minKm2) * u;
}

/**
 * Cột tham chiếu: diện tích thấp, biên độ nhỏ theo mốc (mô phỏng biến động nhẹ trước sự kiện chính).
 */
export function baselineFloodAreaKm2(maxKm2: number, tInWindow01: number): number {
  if (!Number.isFinite(maxKm2) || maxKm2 <= 0) return 0;
  const cap = Math.max(0.01, maxKm2 * 0.14);
  const u = smoothstep01(tInWindow01);
  const wobble = 0.55 + 0.45 * Math.sin(Math.PI * u);
  return Math.min(cap, cap * (0.35 + 0.65 * wobble));
}

const BASELINE_MOCK_DEPTH_MIN_M = 0.04;
const BASELINE_MOCK_DEPTH_MAX_M = 0.55;
const CRISIS_MOCK_DEPTH_FLOOR_M = 0.1;

/**
 * Độ sâu mô phỏng (m) cho tô màu vùng ngập theo thang `floodDepthRgba` — tăng dần khi kéo mốc.
 * Cột tham chiếu: mức nông, đồng bộ cảm giác “trước sự kiện”. Cột đối chiếu: trần theo
 * sâu tối đa từ job (m) nếu có, khoảng dự phòng khi thiếu số.
 */
export function scrubPreviewDepthMeters(
  role: 'baseline' | 'crisis',
  tInWindow01: number,
  jobMaxDepthM: number,
): number {
  const u = smoothstep01(tInWindow01);
  if (role === 'baseline') {
    return BASELINE_MOCK_DEPTH_MIN_M + (BASELINE_MOCK_DEPTH_MAX_M - BASELINE_MOCK_DEPTH_MIN_M) * u;
  }
  const cap =
    Number.isFinite(jobMaxDepthM) && jobMaxDepthM > 0.15 ? Math.min(2.6, jobMaxDepthM * 0.9) : 2.2;
  return CRISIS_MOCK_DEPTH_FLOOR_M + (cap - CRISIS_MOCK_DEPTH_FLOOR_M) * u;
}

export interface ParsedRemoteWindow {
  start: Dayjs;
  end: Dayjs;
  startMs: number;
  endMs: number;
  spanMs: number;
}

/**
 * Chuẩn hóa cửa sổ từ ISO; trả `null` nếu không hợp lệ hoặc end trước start.
 */
export function parseRemoteWindow(w: AoiRemoteTimeWindow): ParsedRemoteWindow | null {
  const start = dayjs(w.startIso);
  const end = dayjs(w.endIso);
  if (!start.isValid() || !end.isValid()) return null;
  const startMs = start.valueOf();
  const endMs = end.valueOf();
  if (endMs < startMs) return null;
  return { start, end, startMs, endMs, spanMs: endMs - startMs };
}

/**
 * `t01` ∈ [0,1] trong cửa sổ — nội suy thời điểm (ms).
 */
export function lerpWindowDateMs(parsed: ParsedRemoteWindow, t01: number): number {
  const t = Math.min(1, Math.max(0, t01));
  return Math.round(parsed.startMs + parsed.spanMs * t);
}

/**
 * Ngày ISO `YYYY-MM-DD` từ mốc ms (theo local dayjs, đồng bộ hiển thị lịch).
 */
export function isoDateFromMs(ms: number): string {
  return dayjs(ms).format('YYYY-MM-DD');
}

/**
 * Suy ra `t01` từ một ngày bất kỳ (ms) so với cửa sổ (clamp).
 */
export function t01FromDateMs(parsed: ParsedRemoteWindow, dateMs: number): number {
  if (parsed.spanMs <= 0) return 0;
  return Math.min(1, Math.max(0, (dateMs - parsed.startMs) / parsed.spanMs));
}

export function t01ToSliderValue(t01: number): number {
  return Math.round(Math.min(1, Math.max(0, t01)) * SLIDER_STEPS);
}

export function sliderValueToT01(v: number): number {
  return Math.min(1, Math.max(0, v / SLIDER_STEPS));
}
